import { useLazyQuery, useQuery } from '@apollo/client'
import { EditCalendar } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { CandidateMeQueryQuery } from '__generated__/graphql'
import {
    CallScheduledInviteeAPI,
    GetCandidateSchedule,
    GetEventDetails,
} from 'candidate/graphql/candidate.gql'
import {
    EmptyStateContainer,
    ScheduleDetailsContainer,
    StyledLink,
} from 'candidate/pages/Dashboard/styled'
import { candidateRouteConfig } from 'candidate/routes'
import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as EmptySchedule } from 'shared/assets/new_empty_schedule.svg'
import DashboardCard from 'shared/components/DashboardCard'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem, parsedDateAndMonth } from 'shared/utils'

interface Props {
    candidate: CandidateMeQueryQuery['candidateMe']
}

interface ScheduleProps {
    schedules: {
        id: string
        candidateId: string
        eventUrl: string
        event: any
        invitee: any
        inviteeUrl: string
        createdAt: string
        updatedAt: string
    }[]
}

const ScheduleCard: React.FC<Props> = ({ candidate }) => {
    const [schedules, setSchedules] = useState<ScheduleProps['schedules']>([])
    const [fetchEventDetails] = useLazyQuery(GetEventDetails)
    const [loading, setLoading] = useState(false)
    const toast = useToast()
    const [fetchInviteeDetails] = useLazyQuery(CallScheduledInviteeAPI)
    const navigate = useNavigate()

    const handleCardClick = () => {
        navigate(candidateRouteConfig.schedule.path)
    }

    useQuery(GetCandidateSchedule, {
        variables: { id: candidate?.id },
        fetchPolicy: 'network-only',
        onCompleted(data) {
            fetchScheduleDetails(data.candidateSchedule)
        },
        onError(error) {
            console.log('error with GetCandidateSchedule query', error)
            toast({
                severity: 'error',
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        },
        skip: !candidate?.id,
    })

    const fetchScheduleDetails = useCallback(
        async (schedules) => {
            if (!schedules || schedules.length === 0) return

            try {
                setLoading(true)
                const eventPromises = schedules.map((schedule) =>
                    fetchEventDetails({
                        variables: { eventId: schedule.eventUrl },
                        fetchPolicy: 'cache-first',
                    })
                )

                const inviteePromises = schedules.map((schedule) =>
                    fetchInviteeDetails({
                        variables: { inviteeId: schedule.inviteeUrl },
                        fetchPolicy: 'cache-first',
                    })
                )

                const eventResults = await Promise.all(eventPromises)
                const inviteeResults = await Promise.all(inviteePromises)

                const fetchedEventsData = eventResults.map(
                    (result) => result.data.callScheduledEventsAPI
                )
                const fetchedInviteesData = inviteeResults.map(
                    (result) => result.data.callScheduledInviteeAPI
                )

                let combined = schedules.map((schedule, index) => ({
                    ...schedule,
                    event: fetchedEventsData[index],
                    invitee: fetchedInviteesData[index],
                }))
                combined = combined.sort(
                    (a, b) =>
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime()
                )
                setSchedules(combined)
            } catch (error) {
                console.error('Error fetching details:', error)
                toast({
                    severity: 'error',
                    title: 'Something went wrong',
                    message: 'Please try again later.',
                })
            } finally {
                setLoading(false)
            }
        },
        [fetchEventDetails, fetchInviteeDetails, toast]
    )

    const activeSchedules = schedules.filter(
        (schedule) => schedule.event?.status === 'active'
    )

    return (
        <DashboardCard title="Schedule" onClick={handleCardClick}>
            <>
                {loading ? (
                    <LoadingPanel />
                ) : activeSchedules.length === 0 ? (
                    <EmptyStateContainer>
                        <EmptySchedule />
                        <Typography variant="body1">
                            You currently have no scheduled meetings.
                        </Typography>
                    </EmptyStateContainer>
                ) : (
                    activeSchedules.map((schedule) => (
                        <StyledLink
                            key={schedule.id}
                            to={candidateRouteConfig.schedule.path}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: getPxAsRem(8),
                                }}
                            >
                                <ScheduleDetailsContainer>
                                    {schedule.event?.event_memberships.map(
                                        (member: any) => (
                                            <Typography
                                                key={member.user_name}
                                                variant="body2"
                                            >
                                                {member.user_name}
                                            </Typography>
                                        )
                                    )}
                                    •
                                    <Typography variant="body2">
                                        {parsedDateAndMonth(
                                            new Date(
                                                schedule.createdAt
                                            ).toLocaleString()
                                        )}
                                    </Typography>
                                </ScheduleDetailsContainer>

                                <Typography variant="bodySans1">
                                    {schedule.event.name}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    padding: getPxAsRem(8),
                                    marginRight: getPxAsRem(16),
                                }}
                            >
                                <EditCalendar
                                    sx={{ display: 'block' }}
                                    color="action"
                                />
                            </Box>
                        </StyledLink>
                    ))
                )}
            </>
        </DashboardCard>
    )
}

export default ScheduleCard
